import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { todayAthens } from "../../../lib/format";
import { StatusPanel } from "./StatusPanel";
import { Phase1Panel } from "./Phase1Panel";
import { HistoryPanel } from "./HistoryPanel";
import { PriceAuditPanel } from "./PriceAuditPanel";
import { TestParams } from "./TestParams";
import { ResultView } from "./ResultView";
import { TestOrderCard } from "./TestOrderCard";
import { CallLog } from "./CallLog";
import { SyncPanel } from "./SyncPanel";
import { PhotoSourceCard } from "./PhotoSourceCard";

const DEFAULT_PARAMS = {
  customer_code: "", item_code: "", date_from: todayAthens(-90), date_to: todayAthens(),
  months: 12, updated_since: "", doc_no: "",
};

export default function SoftOneHealth() {
  const { t } = useI18n();
  const [health, setHealth] = useState(null);
  const [lists, setLists] = useState({ customers: [], items: [] });
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [running, setRunning] = useState("");
  const [result, setResult] = useState(null);
  const [sync, setSync] = useState(null);

  const loadSync = useCallback(() => api.get("/admin/catalogue-sync/status").then((r) => setSync(r.data)).catch(() => {}), []);

  const loadHealth = useCallback(() => api.get("/admin/softone/health").then((r) => setHealth(r.data)).catch(() => setHealth(false)), []);

  useEffect(() => {
    loadHealth();
    loadSync();
    Promise.all([api.get("/customers"), api.get("/catalogue/items")]).then(([c, i]) => {
      setLists({ customers: c.data, items: i.data });
      setParams((p) => ({ ...p, customer_code: c.data[0]?.code || "", item_code: i.data[0]?.code || "" }));
    }).catch(() => toast.error(t("common.error")));
  }, [loadHealth, loadSync, t]);

  useEffect(() => {
    if (!sync?.state?.running) return undefined;
    const timer = setTimeout(loadSync, 3000);
    return () => clearTimeout(timer);
  }, [sync, loadSync]);

  const runMethod = async (method) => {
    setRunning(method);
    try {
      const body = { ...params, months: Number(params.months) || 12 };
      Object.keys(body).forEach((k) => body[k] === "" && delete body[k]);
      const { data } = await api.post("/admin/softone/test", { ...body, method });
      setResult(data);
    } catch (err) {
      setResult({ success: false, method, error: err.response?.data?.detail || t("common.error") });
    } finally {
      setRunning("");
      loadHealth();
    }
  };

  const forceLogin = async () => {
    setRunning("login");
    const { data } = await api.post("/admin/softone/login").catch(() => ({ data: { success: false } }));
    data.success ? toast.success(t("softone.loginOk")) : toast.error(data.error || t("common.error"));
    setRunning("");
    loadHealth();
  };

  if (health === null) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  if (health === false) return <p className="text-sm text-rose-600" data-testid="softone-error">{t("common.error")}</p>;

  return (
    <div className="space-y-6" data-testid="softone-health-page">
      <div>
        <h1 className="text-3xl font-bold text-navy sm:text-4xl">{t("softone.title")}</h1>
        <p className="mt-2 text-sm text-slate-500">{t("softone.subtitle")}</p>
      </div>
      <StatusPanel health={health} onLogin={forceLogin} loggingIn={running === "login"} />
      <Phase1Panel />
      <HistoryPanel />
      <PriceAuditPanel />
      {sync && <SyncPanel sync={sync} onChanged={loadSync} />}
      {sync && <PhotoSourceCard settings={sync.photo_settings} sources={sync.photo_sources} run={sync.photo_run} onChanged={loadSync} />}
      <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
        <h2 className="text-base font-semibold text-navy md:text-lg">{t("softone.testMethods")}</h2>
        <TestParams params={params} setParams={setParams} customers={lists.customers} items={lists.items} />
        <div className="flex flex-wrap gap-2" data-testid="softone-method-buttons">
          {Object.keys(health.methods).map((m) => (
            <button key={m} type="button" disabled={!!running} onClick={() => runMethod(m)} data-testid={`softone-run-${m}`} className={`flex h-11 items-center gap-2 rounded-full px-4 text-sm font-medium transition-colors disabled:opacity-60 ${result?.method === m ? "bg-navy text-white" : "bg-canvas text-navy hover:bg-navy/10"}`}>
              {running === m && <Loader2 className="h-4 w-4 animate-spin" />} {m}
            </button>
          ))}
        </div>
      </section>
      <ResultView result={result} />
      <TestOrderCard customerCode={params.customer_code} itemCode={params.item_code} series={health.settings.test_series} onDone={loadHealth} />
      <CallLog calls={health.recent_calls} />
    </div>
  );
}
